<template>
  <div class="pass-rewards-card">
    <template v-if="userAuthStore.isFreeUser">
      <!-- free user with pass in wallet -->
      <div
        v-if="passes && passes.unusedPass && userAuthStore.isFreeUser"
        class="pass-info pointer"
        tabindex="0"
        role="button"
        :aria-label="`Activate your ${passes.unusedPass.accessDays}-day FB Plus Pass`"
        @click.stop.prevent="activatePlusPassSubmit"
        @keyup.enter.stop.prevent="activatePlusPassSubmit"
      >
        <div class="pass-img">
          <img
            :src="`${userAuthStore.cdn.prefix}/assets/img/rewards/activate.jpg`"
            loading="lazy"
            alt="Activate pass"
          />
        </div>
        <div class="pass-details">
          <div class="pass-title">
            Activate your {{ passes.unusedPass.accessDays }}-day FB Plus Pass
          </div>
          <div class="pass-status">
            <activate-plus-pass-button
              ref="activatePlusPassButtonRef"
              :plus-pass-id="passes.unusedPass.id"
              btn-classes=""
            ></activate-plus-pass-button>
          </div>
        </div>
      </div>

      <!-- free user with no passes -->
      <a v-else-if="!passes && !showPlusPassUnit" class="pass-info" href="/store#store-passes">
        <div class="pass-img">
          <img
            :src="`${userAuthStore.cdn.prefix}/assets/img/rewards/shop.jpg`"
            loading="lazy"
            alt="Shop passes"
          />
        </div>
        <div class="pass-details">
          <div class="pass-title">Shop Passes to</div>
          <div class="pass-status">
            Reactivate
            <span class="fbplus"> <span class="fb">FB</span><span class="plus">Plus</span> </span>
          </div>
        </div>
      </a>

      <!-- user has no purchase/pass -->
      <a v-else-if="showPlusPassUnit" class="pass-info" href="/store#store-passes">
        <div class="pass-img">
          <img
            loading="lazy"
            :src="`${userAuthStore.cdn.prefix}/assets/img/rewards/shop.jpg`"
            alt="Shop passes"
          />
        </div>
        <div class="pass-details">
          <div class="pass-title">
            Earn {{ reward.typeMetaData.redeemThreshold }} Pass Points for a
          </div>
          <div class="pass-status">
            Free {{ reward.typeMetaData.redeemAmount }} Day
            <span class="fbplus"> <span class="fb">FB</span><span class="plus">Plus</span> </span>
            Pass
          </div>
        </div>
      </a>
    </template>

    <!-- active pass -->
    <div v-else class="pass-info">
      <div class="pass-img">
        <img :src="`${userAuthStore.cdn.prefix}/assets/img/rewards/active.jpg`" alt="Active Pass" />
      </div>
      <div class="pass-details">
        <div class="pass-title">Active Pass</div>
        <div class="pass-status">{{ daysRemainingString }}</div>
      </div>
    </div>

    <div class="pass-reward">
      <div v-if="canRedeem" class="reward-status">
        {{ reward.balance }} Pass Points Available
        <div class="reward-status-sub">Redeem Your Reward</div>
      </div>
      <div v-else-if="remainingThreshold" class="reward-status">
        {{ remainingThreshold }} Pass Point{{ remainingThreshold > 1 ? 's' : '' }} Needed
        <div class="reward-status-sub">For Your Next Reward</div>
      </div>

      <!-- no reward -->
      <div v-else class="reward-status">Start Earning Toward a Free Pass</div>

      <div class="reward-status-meter">
        <radial-progress-bar
          radial-id="pass_usage"
          :min="0"
          :max="reward.typeMetaData.redeemThreshold"
          :progress="reward.balance"
          :progress-aria-label="`Progress to Plus Pass Reward`"
          :complete-aria-label="`Redeem Plus Pass Reward`"
          :radius="40"
          icon="redeem"
          :complete-action="canRedeem"
          @completed="redeemReward"
        />
      </div>
    </div>

    <reward-details-expander :reward-id="reward.id">
      <p class="no-pad -tight">
        Earn pass points by using your FB Plus Passes. Gain one new point for each full day of FB
        Pass Plus access. Once you have {{ reward.typeMetaData.redeemThreshold }} pass points, you
        can redeem a free {{ reward.typeMetaData.redeemAmount }}-day pass. Points are calculated
        when the pass expires, or you activate another pass.

        <br /><span class="small"
          >Pass points start from {{ reward.typeMetaData.startDateFormatted }}</span
        >
      </p>
    </reward-details-expander>
  </div>
</template>

<script setup lang="ts">
/**
 * Reward Card
 *
 * x - Auto Renew users should not see X
 * x - Free User + Pass - show activate plus pass X
 * x - - No existing Reward X
 * x - - Existing Reward X
 * x - Plus Pass user should show remaining days X
 * x - No first purchase should show ad for plus pass X
 */
import { computed, ref } from 'vue'
import type { Reward } from '@/types/reward-types'
import { useUserAuthStore } from '@/stores/user-auth'
import RadialProgressBar from '@/components/progress-bars/radial-progress-bar.vue'
import ActivatePlusPassButton from '@/components/buttons/activate-plus-pass-button.vue'
import type { PassUsageStats } from '@/types/calendar-types'
import RewardDetailsExpander from '@/components/users/rewards/reward-details-expander.vue'

/** PROPS **/
const props = defineProps<{
  reward: Reward
  passes: PassUsageStats
}>()

/** STATE **/
const userAuthStore = useUserAuthStore()
const activatePlusPassButtonRef = ref<InstanceType<typeof ActivatePlusPassButton> | null>(null)

/** EMITS **/
const emit = defineEmits(['redeem'])

/** COMPUTED **/
const daysRemainingString = computed((): string => {
  if (props.passes.passDaysRemaining! <= 0) {
    return 'Less than 1 Day Remaining'
  }

  if (props.passes.passDaysRemaining === 1) {
    return '1 Day Remaining'
  }

  return `${props.passes.passDaysRemaining} Days Remaining`
})

const remainingThreshold = computed((): number => {
  if (canRedeem.value) {
    return 0
  }

  return props.reward.typeMetaData.redeemThreshold - props.reward.balance
})

const canRedeem = computed((): boolean => {
  return props.reward.balance >= props.reward.typeMetaData.redeemThreshold
})

const showPlusPassUnit = computed((): boolean => {
  return !userAuthStore.user.hasPurchased && userAuthStore.isFreeUser
})

/**
 * Return unused plus pass if free member and pass available
 */
const passes = computed(() => {
  // never show to subscribers
  if (!userAuthStore.isFreeUser) {
    return null
  }

  // has unused pass
  if (props.passes.totalUnusedDays > 0) {
    return props.passes
  }

  return null
})

/** METHODS **/
function redeemReward() {
  const modalProps = {
    rewardId: props.reward.id,
    heading: 'Redeem FB Plus Pass',
    subheading: `Redeem your ${props.reward.typeMetaData.redeemAmount}-day FB Plus Pass and activate from your FB Wallet at anytime.`,
  }

  emit('redeem', modalProps)
}

// open child component modal
function activatePlusPassSubmit() {
  if (activatePlusPassButtonRef.value) {
    activatePlusPassButtonRef.value.openModal()
  }
}
</script>
